import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { spring } from "./bits";

// ---------- In-app calendar (no native browser/phone pickers) ----------
const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const AppCalendar = ({ value, onChange }) => {
  const { lang } = useLang();
  const locale = lang === "el" ? "el-GR" : "en-GB";
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [cursor, setCursor] = useState(() => {
    if (value) { const [y, m] = value.split("-").map(Number); return new Date(y, m - 1, 1); }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2024, 0, 1 + i))),
    [locale]
  );
  const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(cursor);

  const cells = useMemo(() => {
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < startOffset; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [year, month]);

  const selectedLabel = value
    ? new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${value}T12:00:00`))
    : null;

  return (
    <div className="rounded-3xl bg-white border border-brand/10 p-4 shadow-[0_10px_36px_rgba(134,77,99,0.10)]" data-testid="app-calendar">
      <div className="flex items-center justify-between mb-3">
        <motion.button
          type="button" whileTap={{ scale: 0.85 }} disabled={isCurrentMonth}
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="grid place-items-center h-10 w-10 rounded-full bg-brand-soft/60 text-brand disabled:opacity-25"
          data-testid="app-cal-prev" aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </motion.button>
        <AnimatePresence mode="wait">
          <motion.p
            key={`${year}-${month}`}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}
            className="font-heading text-xl font-bold text-brand-ink capitalize"
            data-testid="app-cal-month"
          >
            {monthLabel}
          </motion.p>
        </AnimatePresence>
        <motion.button
          type="button" whileTap={{ scale: 0.85 }}
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="grid place-items-center h-10 w-10 rounded-full bg-brand-soft/60 text-brand"
          data-testid="app-cal-next" aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </motion.button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase tracking-wide text-brand-ink/40 mb-1.5">
        {weekdays.map((d) => <span key={d}>{d}</span>)}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`grid-${year}-${month}`}
          initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -18 }} transition={{ duration: 0.18 }}
          className="grid grid-cols-7 gap-1"
        >
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} />;
            const dateObj = new Date(year, month, d);
            const dateStr = toStr(dateObj);
            const past = dateObj < today;
            const selected = value === dateStr;
            const isToday = toStr(today) === dateStr;
            return (
              <motion.button
                key={dateStr}
                type="button"
                whileTap={past ? undefined : { scale: 0.82 }}
                transition={spring}
                disabled={past}
                onClick={() => onChange(dateStr)}
                className={`h-11 rounded-xl grid place-items-center text-sm font-bold transition-colors ${
                  selected
                    ? "bg-brand text-brand-cream shadow-[0_8px_20px_rgba(134,77,99,0.35)]"
                    : past
                      ? "text-brand-ink/20"
                      : isToday
                        ? "bg-brand-soft/70 text-brand ring-1 ring-brand/40"
                        : "text-brand-ink/75 active:bg-brand-soft/50"
                }`}
                data-testid={`app-cal-day-${dateStr}`}
              >
                {d}
              </motion.button>
            );
          })}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {selectedLabel && (
          <motion.p
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mt-3 text-center text-sm font-bold text-brand capitalize"
            data-testid="app-cal-selected"
          >
            {selectedLabel}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- In-app clock (wheel picker — any time, shop decides) ----------
const ITEM_H = 46;
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const WheelColumn = ({ items, index, onPick, testid }) => {
  const ref = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (ref.current && !mounted.current) {
      ref.current.scrollTop = index * ITEM_H;
      mounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (!ref.current) return;
    const i = Math.max(0, Math.min(items.length - 1, Math.round(ref.current.scrollTop / ITEM_H)));
    if (i !== index) onPick(i, false);
  };

  const tap = (i) => {
    onPick(i, true);
    ref.current?.scrollTo({ top: i * ITEM_H, behavior: "smooth" });
  };

  return (
    <div className="relative h-[230px] flex-1">
      {/* fade masks */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[70px] z-10 bg-gradient-to-b from-white via-white/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[70px] z-10 bg-gradient-to-t from-white via-white/70 to-transparent" />
      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-hide"
        style={{ paddingTop: (230 - ITEM_H) / 2, paddingBottom: (230 - ITEM_H) / 2, scrollbarWidth: "none", msOverflowStyle: "none" }}
        data-testid={testid}
      >
        {items.map((it, i) => {
          const dist = Math.abs(i - index);
          return (
            <button
              key={it}
              type="button"
              onClick={() => tap(i)}
              style={{ height: ITEM_H }}
              className={`w-full snap-center grid place-items-center font-heading transition-all duration-150 ${
                dist === 0 ? "text-3xl font-bold text-brand" : dist === 1 ? "text-xl font-semibold text-brand-ink/45" : "text-base text-brand-ink/20"
              }`}
              data-testid={`${testid}-${it}`}
            >
              {it}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TimeWheel = ({ value, onChange, defaultValue = "11:00" }) => {
  useEffect(() => { if (!value) onChange(defaultValue); /* eslint-disable-next-line */ }, []);
  const [hh = "11", mm = "00"] = (value || defaultValue).split(":");
  const hIdx = HOURS.indexOf(hh) === -1 ? 11 : HOURS.indexOf(hh);
  const mIdxRaw = MINUTES.indexOf(mm);
  const mIdx = mIdxRaw === -1 ? 0 : mIdxRaw;

  const pickHour = (i) => onChange(`${HOURS[i]}:${MINUTES[mIdx]}`);
  const pickMinute = (i) => onChange(`${HOURS[hIdx]}:${MINUTES[i]}`);

  return (
    <div className="rounded-3xl bg-white border border-brand/10 p-4 shadow-[0_10px_36px_rgba(134,77,99,0.10)]" data-testid="time-wheel">
      <p className="text-center font-heading text-5xl font-semibold text-brand-ink tracking-wide mb-2" data-testid="time-readout">
        {HOURS[hIdx]}<span className="text-brand animate-pulse">:</span>{MINUTES[mIdx]}
      </p>
      <div className="relative">
        {/* center selection band */}
        <div
          className="pointer-events-none absolute inset-x-2 z-10 rounded-2xl bg-brand-soft/50 border border-brand/20"
          style={{ top: (230 - ITEM_H) / 2, height: ITEM_H }}
        />
        <div className="flex items-stretch gap-2 px-6">
          <WheelColumn items={HOURS} index={hIdx} onPick={pickHour} testid="wheel-hour" />
          <div className="grid place-items-center font-heading text-2xl font-bold text-brand/40 pb-1">:</div>
          <WheelColumn items={MINUTES} index={mIdx} onPick={pickMinute} testid="wheel-minute" />
        </div>
      </div>
    </div>
  );
};
